import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface AnalyticsData {
  summary: {
    total_events: number;
    events_24h: number;
    events_7d: number;
    events_30d: number;
    active_users_24h: number;
    active_users_7d: number;
    total_users: number;
    total_organizations: number;
  };
  event_types: Array<{ event_type: string; count: number }>;
  daily_events: Array<{ date: string; count: number }>;
  timestamp: string;
}

export default function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await api.get('/api/analytics/dashboard');
        setData(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const maxDailyCount = Math.max(...data.daily_events.map((d) => d.count), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {new Date(data.timestamp).toLocaleString()}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Events', value: data.summary.total_events },
          { label: 'Events (24h)', value: data.summary.events_24h },
          { label: 'Events (7d)', value: data.summary.events_7d },
          { label: 'Events (30d)', value: data.summary.events_30d },
          { label: 'Active Users (24h)', value: data.summary.active_users_24h },
          { label: 'Active Users (7d)', value: data.summary.active_users_7d },
          { label: 'Total Users', value: data.summary.total_users },
          { label: 'Total Organizations', value: data.summary.total_organizations },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily events chart */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Daily Events (14 days)</h2>
          {data.daily_events.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">No events recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {data.daily_events.map((day) => (
                <div key={day.date} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400 w-24">{day.date}</span>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-purple-600 h-full rounded-full"
                      style={{ width: `${(day.count / maxDailyCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-10 text-right">
                    {day.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Event types */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Top Event Types</h2>
          {data.event_types.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">No event types recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {data.event_types.map((eventType) => (
                <div
                  key={eventType.event_type}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <span className="text-gray-900 dark:text-gray-100 capitalize">{eventType.event_type}</span>
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                    {eventType.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
